'use client';

import * as React from 'react';
import { createContext, useContext, useCallback, useState, useMemo, useRef } from 'react';
import Snackbars from './snackbars';

/**
 * Snackbar options matching mobile API
 */
export interface SnackbarOptions {
    message: string;
    variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
    duration?: number;
    action?: {
        label: string;
        onPress: () => void;
    };
    onDismiss?: () => void;
    icon?: React.ReactNode;
    position?: 'top' | 'bottom';
}

/**
 * Hook return type matching mobile API
 */
export interface UseSnackbarReturn {
    showSnackbar: (options: SnackbarOptions) => void;
    hideSnackbar: () => void;
    updateSnackbar: (options: Partial<SnackbarOptions>) => void;
}

/**
 * Context for snackbar dispatch functions
 */
const SnackbarDispatchContext = createContext<UseSnackbarReturn | null>(null);

interface SnackbarProviderProps {
    children: React.ReactNode;
    maxQueue?: number;
    defaultDuration?: number;
}

/**
 * SnackbarProvider - Matches mobile ui-react-native-core SnackbarProvider API
 *
 * PERFORMANCE OPTIMIZATION:
 * - Context only contains dispatch functions (stable references)
 * - Snackbar state is managed internally
 * - Components using useSnackbar() will NOT re-render when snackbar shows/hides
 *
 * @example
 * ```tsx
 * // In your app root
 * <SnackbarProvider>
 *   <App />
 * </SnackbarProvider>
 *
 * // In any component
 * const { showSnackbar } = useSnackbar();
 * showSnackbar({ message: 'Success!', variant: 'success' });
 * ```
 */
export const SnackbarProvider: React.FC<SnackbarProviderProps> = React.memo(
    ({ children, maxQueue = 3, defaultDuration = 3000 }) => {
        const [currentSnackbar, setCurrentSnackbar] = useState<SnackbarOptions | null>(null);
        const queueRef = useRef<SnackbarOptions[]>([]);
        const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

        const processQueue = useCallback(() => {
            if (queueRef.current.length > 0) {
                const nextSnackbar = queueRef.current.shift();
                if (nextSnackbar) {
                    setCurrentSnackbar(nextSnackbar);

                    const duration = nextSnackbar.duration ?? defaultDuration;
                    if (duration > 0) {
                        if (timeoutRef.current) {
                            clearTimeout(timeoutRef.current);
                        }

                        timeoutRef.current = setTimeout(() => {
                            setCurrentSnackbar(null);
                            setTimeout(processQueue, 0);
                        }, duration);
                    }
                }
            }
        }, [defaultDuration]);

        const showSnackbar = useCallback(
            (options: SnackbarOptions) => {
                const snackbarWithDefaults: SnackbarOptions = {
                    ...options,
                    duration: options.duration ?? defaultDuration,
                    variant: options.variant ?? 'default',
                    position: options.position ?? 'bottom',
                };

                setCurrentSnackbar(prevSnackbar => {
                    if (prevSnackbar === null) {
                        const duration = snackbarWithDefaults.duration ?? defaultDuration;
                        if (duration > 0) {
                            if (timeoutRef.current) {
                                clearTimeout(timeoutRef.current);
                            }

                            timeoutRef.current = setTimeout(() => {
                                setCurrentSnackbar(null);
                                setTimeout(processQueue, 0);
                            }, duration);
                        }
                        return snackbarWithDefaults;
                    } else {
                        if (queueRef.current.length < maxQueue) {
                            queueRef.current.push(snackbarWithDefaults);
                        } else {
                            queueRef.current.shift();
                            queueRef.current.push(snackbarWithDefaults);
                        }
                        return prevSnackbar;
                    }
                });
            },
            [defaultDuration, maxQueue, processQueue]
        );

        const hideSnackbar = useCallback(() => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            setCurrentSnackbar(null);
            setTimeout(processQueue, 0);
        }, [processQueue]);

        const updateSnackbar = useCallback(
            (options: Partial<SnackbarOptions>) => {
                setCurrentSnackbar(prevSnackbar => {
                    if (!prevSnackbar) return null;

                    const updatedSnackbar = {
                        ...prevSnackbar,
                        ...options,
                    };

                    if (options.duration !== undefined) {
                        if (timeoutRef.current) {
                            clearTimeout(timeoutRef.current);
                        }

                        if (updatedSnackbar.duration && updatedSnackbar.duration > 0) {
                            timeoutRef.current = setTimeout(() => {
                                setCurrentSnackbar(null);
                                setTimeout(processQueue, 0);
                            }, updatedSnackbar.duration);
                        }
                    }

                    return updatedSnackbar;
                });
            },
            [processQueue]
        );

        const dispatchValue = useMemo<UseSnackbarReturn>(
            () => ({
                showSnackbar,
                hideSnackbar,
                updateSnackbar,
            }),
            [showSnackbar, hideSnackbar, updateSnackbar]
        );

        React.useEffect(() => {
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }, []);

        return (
            <SnackbarDispatchContext.Provider value={dispatchValue}>
                {children}
                {currentSnackbar && (
                    <Snackbars
                        message={currentSnackbar.message}
                        variant={currentSnackbar.variant}
                        isVisible={true}
                        duration={0} // We handle duration ourselves
                        onClose={hideSnackbar}
                        action={currentSnackbar.action}
                        icon={currentSnackbar.icon}
                        position={currentSnackbar.position}
                    />
                )}
            </SnackbarDispatchContext.Provider>
        );
    }
);

SnackbarProvider.displayName = 'SnackbarProvider';

// No-op fallback for when SnackbarProvider is not in tree
const noopSnackbar: UseSnackbarReturn = {
    showSnackbar: options => {
        console.warn('[Snackbar] showSnackbar called but SnackbarProvider not found. Message:', options.message);
    },
    hideSnackbar: () => {
        // No-op
    },
    updateSnackbar: () => {
        // No-op
    },
};

/**
 * useSnackbar hook - Matches mobile ui-react-native-core API
 *
 * Returns a safe fallback (with console warning) if used outside SnackbarProvider,
 * preventing app crashes while alerting developers.
 *
 * @example
 * ```typescript
 * const { showSnackbar, hideSnackbar } = useSnackbar();
 *
 * const handleSuccess = () => {
 *   showSnackbar({
 *     message: 'Success!',
 *     variant: 'success',
 *     duration: 2000,
 *   });
 * };
 * ```
 */
export const useSnackbar = (): UseSnackbarReturn => {
    const context = useContext(SnackbarDispatchContext);

    // Return safe fallback if not in provider (with dev warning)
    if (context === null) {
        return noopSnackbar;
    }

    return context;
};

// Legacy export for backward compatibility
export const useSnackbarContext = useSnackbar;
